import React, { useState, useEffect } from 'react';
import axios from './utils/axiosConfig';
import { Container, Row, Col, Card, Spinner, Alert, Form, Button, Table, Collapse } from 'react-bootstrap';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useAuth } from './context/AuthContext';
import { MEMBER_TERM } from './config/clubTerms';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

const ActivityParticipationAnalysis = () => {
  const [analysisData, setAnalysisData] = useState(null);
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [storesLoading, setStoresLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedStores, setExpandedStores] = useState({});
  const token = localStorage.getItem('token');

  // 初始化日期為最近三個月
  useEffect(() => {
    const today = new Date();
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(today.getMonth() - 3);
    
    setEndDate(today.toISOString().split('T')[0]);
    setStartDate(threeMonthsAgo.toISOString().split('T')[0]);
  }, []);

  const fetchStores = async () => {
    setStoresLoading(true);
    
    if (!token) {
      setError('請先登入系統才能查看分析資料');
      setStores([]);
      setStoresLoading(false);
      return;
    }
    
    try {
      const response = await axios.get('/activity-participation/stores', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.data && response.data.success && response.data.data) {
        setStores(response.data.data);
      } else {
        setStores([]);
      }
    } catch (error) {
      console.error('Error fetching stores:', error);
      if (error.response?.status === 401) {
        setError('登入已過期，請重新登入');
      } else {
        setError('無法載入社別資料，請稍後再試');
      }
      setStores([]);
    }
    setStoresLoading(false);
  };

  const fetchAnalysisData = async () => {
    setLoading(true);
    
    if (!token) {
      setError('請先登入系統才能查看分析資料');
      setLoading(false);
      return;
    }
    
    try {
      const params = {
        startDate: startDate,
        endDate: endDate
      };
      
      if (selectedStore) {
        params.ss_id = selectedStore;
      }
      
      const response = await axios.get('/activity-participation/activity-participation', {
        headers: { 'Authorization': `Bearer ${token}` },
        params
      });
      
      setAnalysisData(response.data.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching analysis data:', error);
      if (error.response?.status === 401) {
        setError('登入已過期，請重新登入');
      } else {
        setError('無法載入分析資料，請稍後再試。');
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchStores();
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      fetchAnalysisData();
    }
  }, [startDate, endDate, selectedStore]);

  const handleSearch = () => {
    fetchAnalysisData();
  };

  const getCurrentStoreName = () => {
    if (!selectedStore) return '全國';
    const store = stores.find(s => s.ss_id.toString() === selectedStore);
    return store ? store.ss_name : '全國';
  };

  const generatePieChartData = (categories) => {
    if (!categories || categories.length === 0) return null;
    
    // 過濾掉沒有參與人次的類別
    const validCategories = categories.filter(cat => cat.total_participation > 0);
    if (validCategories.length === 0) return null;
    
    const colors = [
      '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
      '#FF9F40', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4',
      '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE'
    ];

    const total = validCategories.reduce((sum, cat) => sum + (cat.total_participation || 0), 0);
    if (total === 0) return null;

    return {
      labels: validCategories.map(cat => {
        const participation = cat.total_participation || 0;
        const percentage = total > 0 ? ((participation / total) * 100).toFixed(1) : '0.0';
        return `${cat.am_name || '未知類別'} (${participation}人次, ${percentage}%)`;
      }),
      datasets: [{
        label: '參與人次',
        data: validCategories.map(cat => cat.total_participation || 0),
        backgroundColor: colors.slice(0, validCategories.length),
        borderColor: colors.slice(0, validCategories.length),
        borderWidth: 1
      }]
    };
  };

  const generateBarChartData = (categories) => {
    if (!categories || categories.length === 0) return null;

    // 過濾掉沒有參與人次的類別
    const validCategories = categories.filter(cat => cat.total_participation > 0);
    if (validCategories.length === 0) return null;

    return {
      labels: validCategories.map(cat => cat.am_name || '未知類別'),
      datasets: [{
        label: '參與人次',
        data: validCategories.map(cat => cat.total_participation || 0),
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1
      }]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 12,
          padding: 8,
          font: {
            size: 11
          }
        }
      },
      title: {
        display: false,
      }
    }
  };

  const barChartOptions = {
    ...chartOptions,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1
        }
      }
    }
  };

  const toggleCategoryExpand = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const toggleStoreExpand = (categoryId, storeId) => {
    const key = `${categoryId}_${storeId}`;
    setExpandedStores(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">載入中...</span>
        </Spinner>
        <p className="mt-2">載入活動參與分析資料中...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <Alert.Heading>無法載入資料</Alert.Heading>
          <p>{error}</p>
          {error.includes('登入') && (
            <>
              <hr />
              <div className="d-flex justify-content-end">
                <Button variant="outline-danger" href="/login">
                  前往登入頁面
                </Button>
              </div>
            </>
          )}
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      <Row className="mb-4">
        <Col>
          <h2 className="text-center">{getCurrentStoreName()}{MEMBER_TERM}參與活動分析</h2>
        </Col>
      </Row>
      
      {/* 查詢條件 */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Body>
              <Row>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label><strong>開始日期：</strong></Form.Label>
                    <Form.Control 
                      type="date" 
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label><strong>結束日期：</strong></Form.Label>
                    <Form.Control 
                      type="date" 
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label><strong>選擇社別：</strong></Form.Label>
                    <Form.Select 
                      value={selectedStore} 
                      onChange={(e) => setSelectedStore(e.target.value)}
                      disabled={storesLoading}
                    >
                      <option value="">全部{MEMBER_TERM}</option>
                      {!storesLoading && stores && stores.length > 0 && stores.map(store => (
                        <option key={store.ss_id} value={store.ss_id}>
                          {store.ss_name}
                        </option>
                      ))}
                      {storesLoading && (
                        <option disabled>載入社別資料中...</option>
                      )}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={2} className="d-flex align-items-end">
                  <Button variant="primary" onClick={handleSearch} className="w-100">
                    查詢
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {analysisData && (
        <>
          {/* 圖表顯示 */}
          <Row className="mb-4">
            <Col lg={6} className="mb-4">
              <Card>
                <Card.Header>
                  <h5>各類別活動參與分佈 (圓餅圖)</h5>
                </Card.Header>
                <Card.Body style={{ height: '400px' }}>
                  {analysisData.categories && analysisData.categories.length > 0 ? (
                    (() => {
                      const chartData = generatePieChartData(analysisData.categories);
                      return chartData ? (
                        <Pie data={chartData} options={chartOptions} />
                      ) : (
                        <div className="d-flex align-items-center justify-content-center h-100">
                          <p className="text-muted">暫無資料可顯示</p>
                        </div>
                      );
                    })()
                  ) : (
                    <div className="d-flex align-items-center justify-content-center h-100">
                      <p className="text-muted">暫無資料可顯示</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col lg={6} className="mb-4">
              <Card>
                <Card.Header>
                  <h5>各類別活動參與分佈 (長條圖)</h5>
                </Card.Header>
                <Card.Body style={{ height: '400px' }}>
                  {analysisData.categories && analysisData.categories.length > 0 ? (
                    (() => {
                      const chartData = generateBarChartData(analysisData.categories);
                      return chartData ? (
                        <Bar data={chartData} options={barChartOptions} />
                      ) : (
                        <div className="d-flex align-items-center justify-content-center h-100">
                          <p className="text-muted">暫無資料可顯示</p>
                        </div>
                      );
                    })()
                  ) : (
                    <div className="d-flex align-items-center justify-content-center h-100">
                      <p className="text-muted">暫無資料可顯示</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* 統計摘要 */}
          <Row className="mb-4">
            <Col>
              <Card>
                <Card.Header>
                  <h5>統計摘要</h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={3} className="text-center mb-3">
                      <h4 className="text-primary">{analysisData.summary?.totalParticipation || 0}</h4>
                      <p>總參與人次</p>
                    </Col>
                    <Col md={3} className="text-center mb-3">
                      <h4 className="text-success">{analysisData.summary?.totalActivities || 0}</h4>
                      <p>總活動數</p>
                    </Col>
                    <Col md={3} className="text-center mb-3">
                      <h4 className="text-info">{analysisData.summary?.totalUniqueParticipants || 0}</h4>
                      <p>參與人數</p>
                    </Col>
                    <Col md={3} className="text-center mb-3">
                      <h4 className="text-warning">{analysisData.summary?.totalCategories || 0}</h4>
                      <p>活動類別數</p>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* 詳細表格 */}
          <Row className="mb-4">
            <Col>
              <Card>
                <Card.Header>
                  <h5>詳細分析表格</h5>
                </Card.Header>
                <Card.Body>
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr className="table-dark">
                        <th>活動類別</th>
                        <th>參與人次</th>
                        <th>活動數量</th>
                        <th>參與人數</th>
                        <th>操作</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analysisData.categories && analysisData.categories.length > 0 ? analysisData.categories.map((category) => (
                        <React.Fragment key={category.am_id}>
                          <tr>
                            <td><strong>{category.am_name}</strong></td>
                            <td><strong>{category.total_participation}</strong></td>
                            <td><strong>{category.total_activities}</strong></td>
                            <td><strong>{category.total_unique_participants}</strong></td>
                            <td>
                              <Button 
                                variant="outline-primary" 
                                size="sm"
                                onClick={() => toggleCategoryExpand(category.am_id)}
                              >
                                {expandedCategories[category.am_id] ? '收合' : '展開'}
                              </Button>
                            </td>
                          </tr>
                          <Collapse in={expandedCategories[category.am_id]}>
                            <tr>
                              <td colSpan="5" className="p-0">
                                <div className="p-3 bg-light">
                                  <h6>各社參與統計</h6>
                                  <Table size="sm" className="mb-3">
                                    <thead>
                                      <tr>
                                        <th>社別</th>
                                        <th>參與人次</th>
                                        <th>活動數量</th>
                                        <th>參與人數</th>
                                        <th>操作</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {category.storeDetails && category.storeDetails.map((store) => (
                                        <React.Fragment key={store.ss_id}>
                                          <tr>
                                            <td>{store.ss_name}</td>
                                            <td>{store.participation_count}</td>
                                            <td>{store.activity_count}</td>
                                            <td>{store.unique_participants}</td>
                                            <td>
                                              <Button 
                                                variant="outline-secondary" 
                                                size="sm"
                                                onClick={() => toggleStoreExpand(category.am_id, store.ss_id)}
                                              >
                                                {expandedStores[`${category.am_id}_${store.ss_id}`] ? '收合' : '展開活動'}
                                              </Button>
                                            </td>
                                          </tr>
                                          <Collapse in={expandedStores[`${category.am_id}_${store.ss_id}`]}>
                                            <tr>
                                              <td colSpan="5" className="p-0">
                                                <div className="p-2 bg-white">
                                                  <h6 className="text-muted">活動明細</h6>
                                                  <Table size="sm" className="mb-0">
                                                    <thead>
                                                      <tr className="table-secondary">
                                                        <th>活動名稱</th>
                                                        <th>活動日期</th>
                                                        <th>參與人次</th>
                                                        <th>參與人數</th>
                                                      </tr>
                                                    </thead>
                                                    <tbody>
                                                      {category.activitiesByStore && 
                                                       category.activitiesByStore
                                                         .find(s => s.ss_id === store.ss_id)?.activities
                                                         .map((activity) => (
                                                        <tr key={activity.a_id}>
                                                          <td>{activity.a_name}</td>
                                                          <td>{activity.a_date}</td>
                                                          <td>{activity.participation_count}</td>
                                                          <td>{activity.unique_participants}</td>
                                                        </tr>
                                                      ))}
                                                    </tbody>
                                                  </Table>
                                                </div>
                                              </td>
                                            </tr>
                                          </Collapse>
                                        </React.Fragment>
                                      ))}
                                    </tbody>
                                  </Table>
                                </div>
                              </td>
                            </tr>
                          </Collapse>
                        </React.Fragment>
                      )) : (
                        <tr>
                          <td colSpan="5" className="text-center text-muted">
                            暫無活動參與資料
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

export default ActivityParticipationAnalysis;